import type { User } from "@/contexts/UserContext";
import {
  RequestError,
  useLazyBackendRequest,
} from "@/hooks/useBackendRequest";
import { useUser } from "@/hooks/useUser";
import { useLogto } from "@logto/react";
import { useEffect, useRef } from "react";

export function useAuth() {
  const { isAuthenticated, error, isLoading, signIn, signOut, getAccessToken } = useLogto();
  const { user, setUser } = useUser();
  const { request: fetchUser } = useLazyBackendRequest<User>(`/users/me`);
  const fetchUserRef = useRef(fetchUser);
  fetchUserRef.current = fetchUser;
  const meFetchExhaustedRef = useRef(false);

  async function login(redirectUrl?: string) {
    const callbackUrl = `${window.location.origin}/callback`;

    if (redirectUrl != null) {
      sessionStorage.setItem("redirectUrl", redirectUrl);
    }

    return signIn(callbackUrl);
  }

  async function logout() {
    await signOut(`${window.location.origin}`);
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
      meFetchExhaustedRef.current = false;
      return;
    }

    if (user != null || meFetchExhaustedRef.current) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const response = await fetchUserRef.current();

        if (cancelled) {
          return;
        }

        if (response.data != null) {
          setUser(response.data);
          return;
        }

        if (
          response.error instanceof RequestError &&
          (response.error.status === 401 || response.error.status === 403)
        ) {
          await signOut(`${window.location.origin}`);
          return;
        }

        meFetchExhaustedRef.current = true;
        console.error("Could not fetch user data, keeping session active", response.error);
      } catch (err) {
        if (!cancelled) {
          meFetchExhaustedRef.current = true;
          console.error("Error fetching user data:", err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, setUser, signOut]);

  return {
    isAuthenticated,
    isLoading,
    error,
    user,
    login,
    logout,
    getAccessToken,
  };
}
